// src/components/ui/OutputPanel.tsx
import React from 'react';
import { Copy, Download, History, ThumbsUp, ThumbsDown } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

interface OutputPanelProps {
  value: string;
  isLoading: boolean;
  onCopy?: () => void;
  onDownload?: () => void;
  onShowHistory?: () => void;
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
  currentRating?: 'thumbs_up' | 'thumbs_down' | null;
}

export const OutputPanel: React.FC<OutputPanelProps> = ({
  value,
  isLoading,
  onCopy,
  onDownload,
  onShowHistory,
  onThumbsUp,
  onThumbsDown,
  currentRating
}) => {
  return (
    <div className="flex-1 bg-white flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-gray-50 min-h-[60px]">
        <h2 className="text-lg font-semibold text-gray-900">Output</h2>
        <div className="flex gap-1">
          {value && !isLoading && onCopy && (
            <button
              onClick={onCopy}
              className="p-1 text-gray-500 hover:text-gray-700 rounded"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          {value && !isLoading && onDownload && (
            <button
              onClick={onDownload}
              className="p-1 text-gray-500 hover:text-gray-700 rounded"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="flex-1 flex flex-col p-2 lg:p-4 max-h-80 lg:max-h-80">
        <div className="h-32 lg:h-40 p-2 lg:p-4 border border-gray-300 rounded-lg bg-white overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <LoadingSpinner size="lg" />
                <p className="text-gray-600 mt-2">Processing your request...</p>
              </div>
            </div>
          ) : value ? (
            <pre className="whitespace-pre-wrap text-gray-900 text-xs lg:text-sm font-mono">
              {value}
            </pre>
          ) : (
            <p className="text-gray-500 italic">
              Your AI-generated DC305 code will appear here...
            </p>
          )}
        </div>
        
        {/* History and Feedback Buttons */}
        <div className="mt-2 lg:mt-4 flex flex-row justify-between items-center gap-2">
          <button
            onClick={onShowHistory}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base flex-shrink-0"
          >
            <History className="w-4 h-4" />
            <span>History</span>
          </button>
          
          {value && !isLoading && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={onThumbsUp}
                className={`p-2 rounded transition-colors ${
                  currentRating === 'thumbs_up'
                    ? 'text-green-600 bg-green-100'
                    : 'text-gray-500 hover:text-green-600'
                }`}
                title="Good result"
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button
                onClick={onThumbsDown}
                className={`p-2 rounded transition-colors ${
                  currentRating === 'thumbs_down'
                    ? 'text-red-600 bg-red-100'
                    : 'text-gray-500 hover:text-red-600'
                }`}
                title="Poor result"
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};